import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    color: '#e2e8f0',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  };

  const contentStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    background: 'rgba(30, 41, 59, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(148, 163, 184, 0.2)'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '40px',
    paddingBottom: '30px',
    borderBottom: '2px solid rgba(34, 211, 238, 0.3)'
  };

  const titleStyle = {
    fontSize: '42px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '10px'
  };

  const subtitleStyle = {
    fontSize: '16px',
    color: '#94a3b8',
    marginTop: '10px'
  };

  const sectionStyle = {
    marginBottom: '40px'
  };

  const h2Style = {
    fontSize: '28px',
    fontWeight: '600',
    color: '#22d3ee',
    marginTop: '40px',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid rgba(34, 211, 238, 0.2)'
  };

  const h3Style = {
    fontSize: '22px',
    fontWeight: '600',
    color: '#60a5fa',
    marginTop: '28px',
    marginBottom: '12px'
  };

  const h4Style = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#7dd3fc',
    marginTop: '20px',
    marginBottom: '10px'
  };

  const pStyle = {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#cbd5e1',
    marginBottom: '16px'
  };

  const ulStyle = {
    marginLeft: '24px',
    marginBottom: '16px',
    color: '#cbd5e1'
  };

  const liStyle = {
    marginBottom: '8px',
    lineHeight: '1.7'
  };

  const warningBoxStyle = {
    background: 'rgba(245, 158, 11, 0.1)',
    border: '2px solid rgba(245, 158, 11, 0.4)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    marginTop: '24px'
  };

  const warningTitleStyle = {
    color: '#fbbf24',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const infoBoxStyle = {
    background: 'rgba(59, 130, 246, 0.1)',
    border: '2px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    marginTop: '24px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px',
    marginBottom: '20px',
    fontSize: '15px'
  };

  const thStyle = {
    background: 'rgba(59, 130, 246, 0.2)',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#22d3ee',
    borderBottom: '2px solid rgba(59, 130, 246, 0.4)'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    color: '#cbd5e1'
  };

  const buttonStyle = {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '30px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Privacy Policy</h1>
          <p style={subtitleStyle}>
            <strong>Effective Date:</strong> January 20, 2026<br />
            <strong>Last Updated:</strong> January 20, 2026<br />
            <strong>Version:</strong> 1.0
          </p>
        </div>

        <section style={sectionStyle}>
          <h2 style={h2Style}>Introduction</h2>
          <p style={pStyle}>
            This Privacy Policy explains how AppleTree ESG Platform ("<strong>AppleTree</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") 
            collects, uses, processes, shares, and protects personal data when companies and users interact with our website, application, and ESG certification services.
          </p>
          <p style={pStyle}>
            We are committed to protecting your privacy and complying with applicable data protection laws, including the European Union's 
            General Data Protection Regulation (GDPR) and other relevant privacy regulations.
          </p>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> Important Notice
            </div>
            <p style={{...pStyle, marginBottom: 0}}>
              Please read this Privacy Policy carefully before using our Platform or submitting any information.
            </p>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. Who We Are (Data Controller)</h2>
          
          <h3 style={h3Style}>1.1 Current Status</h3>
          <p style={pStyle}>
            AppleTree ESG Platform is currently an early-stage project operated by:
          </p>
          <div style={infoBoxStyle}>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Armando Dragoti</strong> (Founder & Operator)</p>
            <p style={{...pStyle, marginBottom: '8px'}}>Email: info@appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: 0}}>Legal Status: Individual operator (pre-incorporation)</p>
          </div>

          <h3 style={h3Style}>1.2 Planned Incorporation</h3>
          <p style={pStyle}>
            We intend to incorporate as a legal entity within the European Union (planned jurisdiction: <strong>Estonia</strong>). 
            Upon incorporation, this Privacy Policy will be updated to reflect the new legal entity's details.
          </p>

          <h3 style={h3Style}>1.3 Applicable Law</h3>
          <p style={pStyle}>Until formal incorporation, AppleTree operates under:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>European Union data protection principles</li>
            <li style={liStyle}>General Data Protection Regulation (GDPR)</li>
            <li style={liStyle}>Italian national privacy laws (where applicable)</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. What Data We Collect</h2>
          <p style={pStyle}>We collect and process the following categories of personal data:</p>

          <h3 style={h3Style}>2.1 Company Submission Data (ESG Questionnaire)</h3>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>Company identification:</strong> Company name, legal form, sector/industry</li>
            <li style={liStyle}><strong>Location data:</strong> Country, city/region, full address (as provided)</li>
            <li style={liStyle}><strong>ESG questionnaire responses:</strong> Environmental, Social, and Governance answers</li>
            <li style={liStyle}><strong>Supporting documentation:</strong> Uploaded files (policies, certificates, reports)</li>
            <li style={liStyle}><strong>Consent preferences:</strong> Map visibility settings, public disclosure preferences</li>
          </ul>

          <h3 style={h3Style}>2.2 Identity & Contact Data</h3>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>Wallet address:</strong> Public blockchain address (Ethereum/Arbitrum compatible)</li>
            <li style={liStyle}><strong>Contact information:</strong> Name, email address, phone number (if provided)</li>
            <li style={liStyle}><strong>Company representatives:</strong> Names and roles of authorized individuals</li>
          </ul>

          <h3 style={h3Style}>2.3 Analytics & Usage Data (via Google Analytics)</h3>
          <p style={pStyle}>We use Google Analytics to understand how users interact with our platform. This service collects:</p>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>Device information:</strong> Browser type, operating system, device type</li>
            <li style={liStyle}><strong>Connection data:</strong> IP address (anonymized), approximate geolocation (country-level)</li>
            <li style={liStyle}><strong>Usage metrics:</strong> Pages visited, session duration, navigation patterns</li>
            <li style={liStyle}><strong>Cookies:</strong> Analytics cookies for tracking sessions and user behavior</li>
          </ul>
          <p style={pStyle}>
            Google Analytics is a third-party service operated by Google LLC. For more information on how Google processes data, 
            visit <a href="https://policies.google.com/privacy" target="_blank" style={{color: '#60a5fa'}}>Google's Privacy Policy</a>.
          </p>

          <h3 style={h3Style}>2.4 Blockchain & Distributed Ledger Data</h3>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>On-chain transactions:</strong> Smart contract interactions, certification events</li>
            <li style={liStyle}><strong>Public identifiers:</strong> Transaction hashes, block numbers, wallet addresses</li>
            <li style={liStyle}><strong>IPFS references:</strong> Content identifiers (CIDs), pinning records</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. How We Use Your Data</h2>
          <p style={pStyle}>We process personal data for the following purposes:</p>

          <h3 style={h3Style}>3.1 Service Delivery</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>ESG certification processing and verification</li>
            <li style={liStyle}>Report generation (JSON/PDF format)</li>
            <li style={liStyle}>Certificate issuance (blockchain-based NFTs)</li>
            <li style={liStyle}>Dashboard access and status updates</li>
          </ul>

          <h3 style={h3Style}>3.2 Transparency & Public Disclosure</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>Public certification registry display</li>
            <li style={liStyle}>IPFS publication of certification reports</li>
            <li style={liStyle}>Blockchain anchoring for immutability</li>
          </ul>

          <h3 style={h3Style}>3.3 Platform Operation & Improvement</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>Security & fraud prevention</li>
            <li style={liStyle}>Technical maintenance and bug fixes</li>
            <li style={liStyle}>Analytics and user experience optimization</li>
            <li style={liStyle}>Customer support and assistance</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>4. Legal Bases for Processing (GDPR)</h2>
          <p style={pStyle}>Where GDPR applies, we rely on the following legal bases:</p>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Legal Basis</th>
                <th style={thStyle}>Processing Activities</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><strong>Contractual Necessity</strong> (Art. 6(1)(b))</td>
                <td style={tdStyle}>ESG questionnaire review, certification, report delivery, customer support</td>
              </tr>
              <tr>
                <td style={tdStyle}><strong>Consent</strong> (Art. 6(1)(a))</td>
                <td style={tdStyle}>Map visibility settings, optional public disclosure, marketing communications, non-essential cookies</td>
              </tr>
              <tr>
                <td style={tdStyle}><strong>Legitimate Interests</strong> (Art. 6(1)(f))</td>
                <td style={tdStyle}>Security & fraud prevention, platform improvement, business communications</td>
              </tr>
              <tr>
                <td style={tdStyle}><strong>Legal Obligation</strong> (Art. 6(1)(c))</td>
                <td style={tdStyle}>Tax obligations, lawful requests from authorities, AML checks</td>
              </tr>
            </tbody>
          </table>
          <p style={pStyle}>
            <strong>You can withdraw consent at any time</strong> by contacting privacy@appletreetoken.com.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. Cookies and Tracking Technologies</h2>
          
          <h3 style={h3Style}>5.1 What Are Cookies?</h3>
          <p style={pStyle}>
            Cookies are small text files stored on your device when you visit our website. We use cookies to improve 
            your experience and understand how you use our platform.
          </p>

          <h3 style={h3Style}>5.2 Cookies We Use</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Cookie Type</th>
                <th style={thStyle}>Purpose</th>
                <th style={thStyle}>Provider</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><strong>Analytics Cookies</strong></td>
                <td style={tdStyle}>Track website usage, user behavior, and performance metrics</td>
                <td style={tdStyle}>Google Analytics</td>
              </tr>
            </tbody>
          </table>

          <h3 style={h3Style}>5.3 Managing Cookies</h3>
          <p style={pStyle}>You can control and delete cookies through your browser settings:</p>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
            <li style={liStyle}><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
            <li style={liStyle}><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
            <li style={liStyle}><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies</li>
          </ul>
          <p style={pStyle}>
            You can also opt-out of Google Analytics tracking by installing the{' '}
            <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" style={{color: '#60a5fa'}}>
              Google Analytics Opt-out Browser Add-on
            </a>.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. Public Transparency, IPFS, and Blockchain Immutability</h2>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> CRITICAL: Please Read Carefully
            </div>
            <p style={{...pStyle, marginBottom: 0}}>
              AppleTree is designed as a transparency-first platform. If your company is approved and certified, 
              certain certification data will be made <strong>publicly accessible</strong> to promote trust and 
              accountability in ESG claims.
            </p>
          </div>

          <h3 style={h3Style}>5.1 What We Publish Publicly</h3>
          <h4 style={h4Style}>Always Public (Non-Negotiable):</h4>
          <ul style={ulStyle}>
            <li style={liStyle}>Company name and website</li>
            <li style={liStyle}>Blockchain wallet address (certification identifier)</li>
            <li style={liStyle}>Certification ID and status</li>
            <li style={liStyle}>ESG scores (Environmental, Social, Governance, Total)</li>
            <li style={liStyle}>Issue date and expiration date</li>
          </ul>

          <h4 style={h4Style}>Location Visibility (Your Choice):</h4>
          <ul style={ulStyle}>
            <li style={liStyle}>✅ Exact address (full street address)</li>
            <li style={liStyle}>✅ City/region only (approximate location)</li>
            <li style={liStyle}>✅ Country only (general location)</li>
            <li style={liStyle}>✅ No location shown on public map</li>
          </ul>

          <h3 style={h3Style}>5.2 IPFS (InterPlanetary File System) Publication</h3>
          <p style={pStyle}>
            IPFS is a decentralized, peer-to-peer file storage system. When we publish a certification report to IPFS:
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}>The report receives a unique <strong>Content Identifier (CID)</strong></li>
            <li style={liStyle}>It can be accessed via multiple IPFS gateways worldwide</li>
            <li style={liStyle}>It may be <strong>replicated</strong> by other IPFS nodes (beyond our control)</li>
          </ul>

          <h3 style={h3Style}>5.3 Blockchain Immutability Warning</h3>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> Blockchain Data is Permanent
            </div>
            <p style={{...pStyle, marginBottom: '12px'}}>
              Once written to the blockchain, transactions <strong>cannot be deleted or altered</strong>.
            </p>
            <p style={{...pStyle, marginBottom: 0}}>
              The transaction will remain visible on public blockchain explorers (Arbiscan, Etherscan) and 
              may be archived by external services indefinitely.
            </p>
          </div>

          <h3 style={h3Style}>5.4 GDPR "Right to Erasure" Limitations</h3>
          <h4 style={h4Style}>What We CAN Do:</h4>
          <ul style={ulStyle}>
            <li style={liStyle}>✅ Remove data from our databases</li>
            <li style={liStyle}>✅ Unpin content from IPFS</li>
            <li style={liStyle}>✅ Remove links from AppleTree website</li>
            <li style={liStyle}>✅ Revoke certification status on-chain</li>
            <li style={liStyle}>✅ Stop displaying your information</li>
          </ul>

          <h4 style={h4Style}>What We CANNOT Guarantee:</h4>
          <ul style={ulStyle}>
            <li style={liStyle}>❌ Full deletion from all IPFS nodes</li>
            <li style={liStyle}>❌ Deletion of blockchain transactions</li>
            <li style={liStyle}>❌ Removal from blockchain explorers</li>
            <li style={liStyle}>❌ Removal from third-party aggregators</li>
          </ul>

          <div style={infoBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px'}}>
              By submitting a certification request, you explicitly acknowledge and accept:
            </p>
            <ul style={ulStyle}>
              <li style={liStyle}>Certification data will be published publicly for transparency</li>
              <li style={liStyle}>IPFS and blockchain publication may result in persistent records</li>
              <li style={liStyle}>AppleTree will make best efforts to honor deletion requests, but cannot guarantee complete removal</li>
              <li style={liStyle}>You have informed and obtained consent from individuals whose data is included</li>
            </ul>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. How We Share Your Data</h2>
          <p style={pStyle}>We may share your personal data with the following categories of recipients:</p>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Provider Type</th>
                <th style={thStyle}>Examples</th>
                <th style={thStyle}>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>Hosting & Infrastructure</td>
                <td style={tdStyle}>Heroku, Netlify, AWS</td>
                <td style={tdStyle}>Application hosting, database storage</td>
              </tr>
              <tr>
                <td style={tdStyle}>IPFS Storage</td>
                <td style={tdStyle}>Pinata, Infura</td>
                <td style={tdStyle}>Decentralized file storage</td>
              </tr>
              <tr>
                <td style={tdStyle}>Blockchain Networks</td>
                <td style={tdStyle}>Arbitrum, Ethereum</td>
                <td style={tdStyle}>On-chain certification recording</td>
              </tr>
              <tr>
                <td style={tdStyle}>Email Delivery</td>
                <td style={tdStyle}>SendGrid, Mailgun</td>
                <td style={tdStyle}>Transactional emails, notifications</td>
              </tr>
              <tr>
                <td style={tdStyle}>Mapping Services</td>
                <td style={tdStyle}>Mapbox</td>
                <td style={tdStyle}>Public certification map display</td>
              </tr>
            </tbody>
          </table>

          <p style={pStyle}>
            <strong>We do NOT sell personal data</strong> to third parties for marketing purposes.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Data Retention</h2>
          <p style={pStyle}>We retain personal data only as long as necessary:</p>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Data Category</th>
                <th style={thStyle}>Retention Period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>Company submissions (approved)</td>
                <td style={tdStyle}>Indefinitely (or until deletion request)</td>
              </tr>
              <tr>
                <td style={tdStyle}>Company submissions (rejected)</td>
                <td style={tdStyle}>24 months after rejection</td>
              </tr>
              <tr>
                <td style={tdStyle}>Admin review logs</td>
                <td style={tdStyle}>5 years</td>
              </tr>
              <tr>
                <td style={tdStyle}>Technical logs (IP, usage)</td>
                <td style={tdStyle}>90 days (default)</td>
              </tr>
              <tr>
                <td style={tdStyle}>Blockchain transactions</td>
                <td style={tdStyle}>Permanent (immutable)</td>
              </tr>
              <tr>
                <td style={tdStyle}>IPFS pinned content</td>
                <td style={tdStyle}>Until unpinned or revoked</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Your Data Protection Rights</h2>
          <p style={pStyle}>
            Depending on your location and applicable laws, you may have the following rights under GDPR:
          </p>

          <ul style={ulStyle}>
            <li style={liStyle}><strong>Right to Access (Art. 15):</strong> Request a copy of your personal data</li>
            <li style={liStyle}><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete data</li>
            <li style={liStyle}><strong>Right to Erasure (Art. 17):</strong> Request deletion (subject to blockchain/IPFS limitations)</li>
            <li style={liStyle}><strong>Right to Restriction (Art. 18):</strong> Limit how we use your data</li>
            <li style={liStyle}><strong>Right to Portability (Art. 20):</strong> Receive data in machine-readable format</li>
            <li style={liStyle}><strong>Right to Object (Art. 21):</strong> Object to processing based on legitimate interests</li>
            <li style={liStyle}><strong>Right to Withdraw Consent (Art. 7(3)):</strong> Withdraw consent at any time</li>
            <li style={liStyle}><strong>Right to Complain:</strong> File a complaint with your Data Protection Authority</li>
          </ul>

          <div style={infoBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '8px'}}>How to Exercise Your Rights:</p>
            <p style={{...pStyle, marginBottom: '8px'}}>Email: info@appletreetoken.com (or privacy@appletreetoken.com)</p>
            <p style={{...pStyle, marginBottom: '8px'}}>Subject: "Data Subject Request – [Your Company Name]"</p>
            <p style={{...pStyle, marginBottom: 0}}>Response Time: Within 30 days</p>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Contact Information</h2>
          <div style={infoBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px'}}>For Privacy Inquiries:</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Email:</strong> info@appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Alias:</strong> privacy@appletreetoken.com (forwards to info@)</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Website:</strong> https://appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: 0}}><strong>Response Time:</strong> Within 5 business days (acknowledgment), 30 days (full response)</p>
          </div>

          <h3 style={h3Style}>Data Protection Authority (Italy)</h3>
          <p style={pStyle}>
            <strong>Garante per la Protezione dei Dati Personali</strong><br />
            Piazza Venezia, 11 - 00187 Roma<br />
            Website: www.garanteprivacy.it
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Updates to This Policy</h2>
          <p style={pStyle}>
            We may update this Privacy Policy periodically. Material changes will be communicated via email 
            and a prominent notice on the Platform.
          </p>
          <p style={pStyle}>
            <strong>Last Reviewed:</strong> January 20, 2026<br />
            <strong>Next Scheduled Review:</strong> July 20, 2026
          </p>
        </section>

        <div style={{textAlign: 'center', marginTop: '40px', paddingTop: '30px', borderTop: '2px solid rgba(148, 163, 184, 0.2)'}}>
          <p style={{...pStyle, fontSize: '14px', color: '#94a3b8', marginBottom: '20px'}}>
            By using the AppleTree Platform, you acknowledge that you have read, understood, and agree to this Privacy Policy.
          </p>
          <button 
            style={buttonStyle}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            onClick={() => navigate(-1)}
          >
            ← Back to Platform
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
